import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import uniqueId from 'lodash/fp/uniqueId';

import { styled } from '@prose-ui/legacy';
import { animated, useSpring } from '@react-spring/web';

import { Typography } from 'Components/Typography';

import arrowGrey from 'assets/images/arrow_grey.svg';
import arrowNoir from 'assets/images/arrow_noir.svg';
import arrowWhite from 'assets/images/arrow_white.svg';

import useContentHeight from 'utils/useContentHeight';
import useIsMounted from 'utils/useIsMounted';

const AnswerTypography = styled(Typography, {
  shouldForwardProp: props => props !== 'keepLineBreaks',
})`
  ${({ keepLineBreaks }) => (keepLineBreaks ? 'white-space: pre-line;' : '')}
`;

const StyledTypography = styled(Typography)`
  --url-light: url(${arrowGrey});
  --url-dark: url(${arrowWhite});
  --url-noir: url(${arrowNoir});
  appearance: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  position: relative;
  padding-right: 60px;
  text-align: left;
  width: 100%;
  &:after {
    content: ' ';
    width: 20px;
    height: 20px;
    background-image: var(--url-variant);
    background-repeat: no-repeat;
    background-position: center center;
    display: inline-block;
    position: absolute;
    transition: transform 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
    right: 0;
    top: 0;
    padding: var(--chevron-padding);
  }
`;

const OpenedTypography = styled(StyledTypography)`
  margin-bottom: 12px;
  cursor: pointer;
  &:after {
    transform: rotate(180deg);
  }
`;

const StyledAnimatedDiv = styled(animated.div)`
  overflow: hidden;
  // if not specified, the animated div has default margin causing the height of the text box to be slightly cropped
  & p {
    margin: 0;
  }
`;

const Collapse = ({
  className,
  dataFrom,
  dataClick,
  question,
  answer,
  variant,
  scrollIntoViewOnOpen,
  style,
  questionTypographyVariant,
  keepLineBreaks,
  markupName,
  isOpen,
  onClick,
  questionStyle,
  chevronPadding,
}) => {
  const containerRef = useRef();
  const ref = useRef();
  const [isOpened, setOpened] = useState(isOpen);
  const height = useContentHeight(ref, isOpened);
  const isMounted = useIsMounted();

  const props = useSpring({
    height: isOpened ? height : 0,
    from: 0,
  });

  useEffect(() => {
    setOpened(isOpen);
  }, [isOpen]);

  const toggleOpenState = () => {
    onClick?.();
    setOpened(!isOpened);
    if (scrollIntoViewOnOpen && !isOpened) {
      setTimeout(
        () =>
          containerRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          }),
        200
      );
    }
  };

  const questionId = uniqueId();

  const Question = isOpened ? OpenedTypography : StyledTypography;

  return (
    <div ref={containerRef} className={className} style={style}>
      <Question
        aria-controls={`panel-${questionId}`}
        aria-expanded={isOpened}
        color={variant === 'dark' ? 'white' : 'noir'}
        data-click={dataClick}
        data-from={dataFrom}
        id={`button-${questionId}`}
        markupName={markupName}
        onClick={toggleOpenState}
        style={{
          '--url-variant': `var(--url-${variant})`,
          '--chevron-padding': chevronPadding,
          ...questionStyle,
        }}
        type="button"
        variant={questionTypographyVariant}
      >
        {question}
      </Question>
      {/* check isMounted to avoid state update on unmounted component error */}
      <StyledAnimatedDiv
        aria-labelledby={`button-${questionId}`}
        id={`panel-${questionId}`}
        style={isMounted.current === true ? props : undefined}
      >
        <AnswerTypography
          ref={ref}
          color={variant === 'dark' ? 'white' : 'noir'}
          keepLineBreaks={keepLineBreaks}
          markupName="p"
          variant="p2"
        >
          {answer}
        </AnswerTypography>
      </StyledAnimatedDiv>
      <br />
    </div>
  );
};

Collapse.defaultProps = {
  variant: 'light',
  dataFrom: 'collapse',
  questionTypographyVariant: 'p1',
  style: {},
  dataClick: 'toggle-collapse',
  keepLineBreaks: false,
  markupName: 'button',
  isOpen: false,
  scrollIntoViewOnOpen: false,
  questionStyle: {},
  chevronPadding: '0px',
};

Collapse.propTypes = {
  className: PropTypes.string,
  chevronPadding: PropTypes.string,
  questionStyle: PropTypes.shape({}),
  classes: PropTypes.objectOf(PropTypes.string),
  dataFrom: PropTypes.string,
  question: PropTypes.node.isRequired,
  variant: PropTypes.oneOf(['light', 'dark', 'noir']),
  answer: PropTypes.node.isRequired,
  questionTypographyVariant: PropTypes.string,
  style: PropTypes.shape({}),
  dataClick: PropTypes.string,
  keepLineBreaks: PropTypes.bool,
  isOpen: PropTypes.bool,
  scrollIntoViewOnOpen: PropTypes.bool,
  markupName: PropTypes.string,
  onClick: PropTypes.func,
};
export default Collapse;
